import React, { createContext, useContext, useState, useCallback } from 'react'
import { atom, useAtom } from 'jotai'
import type { Message } from '../content/RevenueWaterfallInspector/components/Chat/ai/useAiChat'

export interface TableSelectionData {
  rowCount: number
  columnCount: number
  rowLabels: string[]
  columnLabels: string[]
  cellData: Array<{
    row: number
    col: number
    value: string
    rowLabel: string
    columnLabel: string
  }>
}

interface TableToChatContextType {
  addSelectionToChat: (selectionData: TableSelectionData) => void
  lastAddedSelection: TableSelectionData | null
  clearLastSelection: () => void
}

const TableToChatContext = createContext<TableToChatContextType | undefined>(
  undefined
)

export const useTableToChat = () => {
  const context = useContext(TableToChatContext)
  if (!context) {
    throw new Error('useTableToChat must be used within a TableToChatProvider')
  }
  return context
}

interface TableToChatProviderProps {
  children: React.ReactNode
}

export const TableToChatProvider: React.FC<TableToChatProviderProps> = ({
  children
}) => {
  const [lastAddedSelection, setLastAddedSelection] =
    useState<TableSelectionData | null>(null)

  // Persistent Chat state across mounts
  const [, setSelectedContextItems] = useAtom(chatSelectedContextItemsAtom)
  const [, setCurrentTableSelection] = useAtom(chatCurrentTableSelectionAtom)
  const [, setContextLocked] = useAtom(chatContextLockedAtom)

  const addSelectionToChat = useCallback(
    (selectionData: TableSelectionData) => {
      setLastAddedSelection(selectionData)
      // Prepare atoms so Chat sees pills immediately on mount
      setCurrentTableSelection(selectionData)
      // Reset context to only Revenue Waterfall and current table selection
      setSelectedContextItems(
        new Set<string>(['revenue:waterfall', 'table-selection:current'])
      )
      // Lock further auto additions
      setContextLocked(true)
    },
    [setCurrentTableSelection, setSelectedContextItems, setContextLocked]
  )

  const clearLastSelection = useCallback(() => {
    setLastAddedSelection(null)
  }, [])

  return (
    <TableToChatContext.Provider
      value={{
        addSelectionToChat,
        lastAddedSelection,
        clearLastSelection
      }}
    >
      {children}
    </TableToChatContext.Provider>
  )
}

// Atoms to persist Chat UI state across inspector mounts
export const chatSelectedContextItemsAtom = atom<Set<string>>(
  new Set<string>(['revenue:waterfall'])
)
export const chatCurrentTableSelectionAtom = atom<TableSelectionData | null>(
  null
)

// Lock to prevent auto adding extra context (used when "Ask AI" from table is clicked)
export const chatContextLockedAtom = atom<boolean>(false)

// Persist AI chat state across inspector mounts
export const chatMessagesAtom = atom<Message[]>([])
export const chatInputDraftAtom = atom<string>('')
